import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, useMotionValue, useSpring } from "motion/react";
import * as m from "motion/react-m";
import type { StoryCard as Story } from "../api/client";
import { Favicon } from "./Favicon";
import { when } from "../lib/format";

const TICK_MS = 4600;
const HEADLINES = 5;

/** Compact hero for the Today view: drifting aurora blobs that also lean toward the
 * pointer, and a ticker cycling the freshest stories — favicon, source, snippet, link.
 * A progress bar marks the rhythm; dots let the reader jump. Hover pauses the rotation.
 * transform/opacity only; reduced-motion users get instant swaps and a still aurora. */
export function TodayHero({ items }: { items: Story[] }) {
  const headlines = items.slice(0, HEADLINES);
  const [i, setI] = useState(0);
  const [paused, setPaused] = useState(false);
  const px = useMotionValue(0);
  const py = useMotionValue(0);
  const sx = useSpring(px, { stiffness: 45, damping: 16 });
  const sy = useSpring(py, { stiffness: 45, damping: 16 });
  useEffect(() => {
    if (paused || headlines.length < 2) return;
    const t = setInterval(() => setI((v) => (v + 1) % headlines.length), TICK_MS);
    return () => clearInterval(t);
  }, [headlines.length, paused]);
  const lead = headlines[i];
  const today = new Date().toLocaleDateString(undefined, {
    weekday: "long",
    day: "numeric",
    month: "long",
  });
  return (
    <m.section
      className="hero"
      aria-label="Today"
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, ease: "easeOut" }}
      onPointerMove={(e) => {
        const r = e.currentTarget.getBoundingClientRect();
        px.set(((e.clientX - r.left) / r.width - 0.5) * 22);
        py.set(((e.clientY - r.top) / r.height - 0.5) * 14);
      }}
      onPointerEnter={() => setPaused(true)}
      onPointerLeave={() => {
        setPaused(false);
        px.set(0);
        py.set(0);
      }}
    >
      <m.div className="hero-parallax" aria-hidden="true" style={{ x: sx, y: sy }}>
        <m.div
          className="hero-blob a"
          animate={{ x: [0, 26, -14, 0], y: [0, -18, 10, 0] }}
          transition={{ repeat: Infinity, duration: 14, ease: "easeInOut" }}
        />
        <m.div
          className="hero-blob b"
          animate={{ x: [0, -22, 12, 0], y: [0, 14, -16, 0] }}
          transition={{ repeat: Infinity, duration: 18, ease: "easeInOut" }}
        />
      </m.div>
      <p className="mono hero-kicker">{today}</p>
      <AnimatePresence mode="wait" initial={false}>
        {lead && (
          <m.div key={lead.id} className="hero-line">
            <Link className="hero-link" to={`/stories/${lead.id}`}>
              <m.p
                className="hero-headline"
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -14 }}
                transition={{ duration: 0.35, ease: "easeOut" }}
              >
                {lead.title}
              </m.p>
              {lead.snippet && (
                <m.p
                  className="hero-snip"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  {lead.snippet}
                </m.p>
              )}
              <m.p
                className="hero-source mono"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.25 }}
              >
                <Favicon domain={lead.source} />
                {lead.source} · {when(lead.last_activity_at)}
                {lead.sources > 1 && ` · ${lead.sources} sources`}
              </m.p>
            </Link>
          </m.div>
        )}
      </AnimatePresence>
      {headlines.length > 1 && (
        <div className="hero-nav">
          {!paused && (
            <m.div
              key={i}
              className="hero-progress"
              initial={{ scaleX: 0 }}
              animate={{ scaleX: 1 }}
              transition={{ duration: TICK_MS / 1000, ease: "linear" }}
            />
          )}
          <div className="hero-dots">
            {headlines.map((h, d) => (
              <m.button
                key={h.id}
                type="button"
                className={`hero-dot${d === i ? " on" : ""}`}
                aria-label={`Show headline ${d + 1}: ${h.title}`}
                onClick={() => setI(d)}
                whileTap={{ scale: 0.8 }}
              />
            ))}
          </div>
        </div>
      )}
    </m.section>
  );
}
